import { useState, useEffect, useCallback } from 'react';
import { SamaritanMemberSmallTemplate } from './useMemberSearch';

export type TagType = 'green' | 'red' | 'blue';

export type GroupData = {
    id: number;
    title: string;
    tags: [string, TagType][];
    stats: [string, number][];
    startDate: string;
};

export interface GroupMemberState {
    members: SamaritanMemberSmallTemplate[] | null;
    error: Error | null;
    isLoading: boolean;
}

export interface GroupMemberHookReturn {
    // Group data
    groups: GroupData[] | null;
    groupsError: Error | null;
    isGroupsLoading: boolean;
    refetchGroups: () => void;
    
    // Member data per group
    getGroupMemberState: (groupId: number) => GroupMemberState;
    
    // Actions
    fetchMembers: (groupId: number) => void;
    clearMembers: (groupId: number) => void;
}

export const useGroupMemberHook = (
    fetchGroupsAPI: () => Promise<Response>,
    fetchMembersAPI: (groupId: number) => Promise<Response>
): GroupMemberHookReturn => {
    // Group state
    const [groups, setGroups] = useState<GroupData[] | null>(null);
    const [groupsError, setGroupsError] = useState<Error | null>(null);
    const [isGroupsLoading, setIsGroupsLoading] = useState(false);
    
    // Per-group member state
    const [groupMemberStates, setGroupMemberStates] = useState<Map<number, GroupMemberState>>(new Map());

    // Fetch groups
    const fetchGroups = useCallback(() => {
        setGroupsError(null);
        setIsGroupsLoading(true);

        fetchGroupsAPI()
            .then((res: Response) => {
                if (!res.ok) {
                    throw new Error(`Error: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then((data: { groups: GroupData[] }) => {
                setGroups(data.groups);
            })
            .catch((e: Error) => {
                setGroupsError(e);
                setGroups(null);
            })
            .finally(() => {
                setIsGroupsLoading(false);
            });
    }, [fetchGroupsAPI]);

    // Get member state for a specific group
    const getGroupMemberState = useCallback((groupId: number): GroupMemberState => {
        return groupMemberStates.get(groupId) || {
            members: null,
            error: null,
            isLoading: false
        };
    }, [groupMemberStates]);

    // Fetch members for a specific group
    const fetchMembers = useCallback((groupId: number) => {
        setGroupMemberStates(prev => new Map(prev.set(groupId, {
            members: prev.get(groupId)?.members || null,
            error: null,
            isLoading: true
        })));

        fetchMembersAPI(groupId)
            .then((res: Response) => {
                if (!res.ok) {
                    throw new Error(`Error: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then((data: { members: SamaritanMemberSmallTemplate[] }) => {
                setGroupMemberStates(prev => new Map(prev.set(groupId, {
                    members: data.members,
                    error: null,
                    isLoading: false
                })));
            })
            .catch((e: Error) => {
                setGroupMemberStates(prev => new Map(prev.set(groupId, {
                    members: null,
                    error: e,
                    isLoading: false
                })));
            });
    }, [fetchMembersAPI]);

    // Clear members for a specific group
    const clearMembers = useCallback((groupId: number) => {
        setGroupMemberStates(prev => {
            const newMap = new Map(prev);
            newMap.delete(groupId);
            return newMap;
        });
    }, []);

    // Auto-fetch groups on mount
    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    return {
        // Group data
        groups,
        groupsError,
        isGroupsLoading,
        refetchGroups: fetchGroups,
        
        // Member data per group
        getGroupMemberState,
        
        // Actions
        fetchMembers,
        clearMembers
    };
}; 